<template>
    <!-- Start Action Items -->
    <div>
        <div
            class="clickable fake-link"
            @click="expandRecommendation = !expandRecommendation"
            @keypress.enter="expandRecommendation = !expandRecommendation"
        >
            {{ $t("surveys.actionItem.recommendations") }}
            <i
                :class="
                    expandRecommendation ? 'fal fa-caret-up tw-ml-2' : 'fal fa-caret-right tw-ml-2'
                "
            ></i>
        </div>
        <div v-show="expandRecommendation" class="tw-m-0 tw-p-0" style="padding: 0;">
            <hr />
            <div v-for="item in sortedRecommendations" :key="item._id">
                <div class="tw-flex tw-items-center tw-justify-center tw-space-x-8">
                    <div class="tw-flex-1 tw-truncate">
                        <a
                            v-b-popover.hover.top="{
                                content: item.content,
                            }"
                            href="#"
                            class="tw-block fake-link tw-truncate"
                            @click.prevent="() => openActionItemModal(item)"
                        >
                            {{ item.content }}
                        </a>
                        <div
                            class="tw-flex tw-space-x-4 tw-justify-start tw-items-start tw-text-gray-400"
                        >
                            <span v-if="item.recommended" class="color-success">
                                <i class="fal fa-lightbulb tw-mr-1"></i>
                                {{ $t("surveys.actionItem.labelRecommended") }}
                            </span>
                            <span v-if="item.category && item.category.name">
                                <i class="fal fa-tag tw-mr-1"></i>
                                {{ item.category.name }}
                            </span>
                        </div>
                    </div>
                    <button
                        class="btn btn-sm btn-primary btn-raised"
                        @click="
                            () => {
                                openActionItemModal(item);
                            }
                        "
                    >
                        {{ $t("surveys.actionItem.buttonShowDetails") }}
                    </button>
                </div>
                <div class="hr-small"></div>
            </div>
        </div>
        <hr v-show="!expandRecommendation" />
        <div v-show="!actionItems.length" class="text-center">
            <img alt="" :src="emptyIcon" class="placeholder-list" />
            <h4 class="color-grey">
                <span>{{ $t("surveys.actionItem.labelNoData") }} </span>
            </h4>
        </div>
        <div v-for="item in sortedActionItemList" :key="item._id">
            <div class="tw-flex tw-items-center tw-justify-center tw-space-x-8">
                <div class="tw-flex-1 tw-truncate">
                    <a
                        v-b-popover.hover.top="{
                            content: item.content,
                        }"
                        href="#"
                        class="tw-block fake-link tw-truncate"
                        @click.prevent="() => openActionItemModal(item)"
                    >
                        {{ item.content }}
                    </a>

                    <div
                        class="tw-flex tw-space-x-4 tw-justify-start tw-items-start tw-text-gray-400"
                    >
                        <span
                            v-if="!disabled(item)"
                            class="clickable"
                            @click="
                                () => {
                                    if (item.status === 'done') {
                                        item.status = 'open';
                                    } else {
                                        item.status = 'done';
                                    }
                                    actionItem = item;
                                    doUpdateActionItem();
                                }
                            "
                            @keypress.enter="
                                () => {
                                    if (item.status === 'done') {
                                        item.status = 'open';
                                    } else {
                                        item.status = 'done';
                                    }
                                    actionItem = item;
                                    doUpdateActionItem();
                                }
                            "
                        >
                            <i
                                class="tw-mr-2"
                                :class="{
                                    'fal fa-square tw-text-gray-400': item.status !== 'done',
                                    'fas fa-check-square tw-text-green-700': item.status === 'done',
                                }"
                            ></i>
                            {{ $t("surveys.actionItem.labelMarkComplete") }}
                        </span>
                        <span
                            v-if="item.deadline && item.status !== 'done'"
                            :title="item.deadline | moment('MMM DD, YYYY')"
                        >
                            <i class="fal fa-calendar tw-mr-1" /> Due
                            {{ item.deadline | moment("from", "now") }}</span
                        >
                        <span>
                            <i class="fal fa-comment tw-mr-1" />
                            {{ item.comments.length }} Comments
                        </span>
                        <span v-if="item.category">
                            <i class="fal fa-tag tw-mr-1" />
                            {{ item.category.name }}
                        </span>
                        <span>
                            <i class="fal fa-users tw-mr-1"></i>
                            {{ typeLabels(item.type) }}
                            <span
                                v-if="
                                    item.type === 'surveyTeamActionItem' &&
                                        item.localTeams &&
                                        item.localTeams.length
                                "
                            >
                                <span v-for="(team, idx) in item.localTeams" :key="team._id">
                                    {{ team.name }}
                                    {{ `${idx !== item.localTeams.length - 1 ? "," : ""}` }}
                                </span>
                            </span>
                        </span>
                    </div>
                </div>

                <div class="tw-justify-end tw-flex tw-flex-wrap tw--space-x-1">
                    <l-profile-picture
                        v-for="owner in item.owners"
                        :key="`${item._id}_${owner._id}`"
                        size="xs"
                        :user="owner"
                        :show-popover-name="true"
                        class="tw-inline-block"
                        img-classes="tw-ring-2 tw-ring-white"
                    />
                </div>
            </div>
            <div class="hr-small"></div>
        </div>

        <l-modal
            v-if="showModal && editMode(actionItem)"
            size="l"
            :close-on-backdrop-click="false"
            @close="closeActionItemModal"
        >
            <template #header>
                <span
                    v-if="actionItem.type !== 'surveyActionItemSuggestion'"
                    class="r-default-margin-s"
                >
                    <l-profile-picture
                        v-for="(owner, idx) in actionItem.owners.slice(0, 5)"
                        :key="`${actionItem._id}_${owner._id}`"
                        size="s"
                        :user="owner"
                        :show-popover-name="true"
                        :class="[{ 'tw--ml-1': idx !== 0 }, 'tw-inline-block']"
                        img-classes="tw-ring-2 tw-ring-white"
                    />
                    {{ actionItem.owners.length > 5 ? `...` : "" }}
                </span>
                <span
                    v-b-popover.hover.bottom="{
                        content: actionItem.content,
                    }"
                >
                    {{ actionItem.content || $t("surveys.actionItemModal.title") }}
                </span>
            </template>
            <template #body>
                <div class="tw-p-4">
                    <div class="row">
                        <div class="col-md-12">
                            <l-input-text
                                v-model="actionItem.content"
                                :label="``"
                                :placeholder="$t('surveys.actionItemModal.placeholderContent')"
                                type="text"
                                :show-text-format-hint="true"
                            ></l-input-text>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <l-rich-text-editor
                                v-model="actionItem.description"
                                :content="actionItem.description"
                            ></l-rich-text-editor>
                        </div>
                    </div>

                    <l-space :size="2" />

                    <div class="row">
                        <div class="col-md-6">
                            <l-select
                                v-model="actionItem.type"
                                :show-help-block="true"
                                :options="typeOptions"
                                :label="$t(`surveys.actionItemModal.labelType`)"
                                :placeholder="$t(`surveys.actionItemModal.placeholderType`)"
                            />
                        </div>

                        <div class="col-md-6">
                            <l-select
                                v-model="actionItem.visibility"
                                :show-help-block="true"
                                :options="visibilityOptions"
                                :label="$t('surveys.actionItemModal.labelVisibility')"
                            />
                        </div>
                    </div>
                    <div class="row">
                        <div v-if="actionItem.type === 'surveyTeamActionItem'" class="col-md-12">
                            <l-input-typeahead
                                :label="$t(`surveys.actionItemModal.labelTeam`)"
                                :placeholder="$t(`surveys.actionItemModal.placeholderTeam`)"
                                :data="teamOrUser().filter(a => a.type === 'team')"
                                :on-select="p => addTeams(p)"
                                :on-input-change="typeaheadParticipantOnInputChange"
                            />
                        </div>
                        <div
                            v-if="
                                actionItem.type === 'surveyTeamActionItem' && actionItem.localTeams
                            "
                            class="col-md-12"
                        >
                            <span
                                v-for="team in actionItem.localTeams"
                                :key="team._id"
                                class="tw-mr-1"
                            >
                                <button
                                    style="border: none"
                                    class="tw-p-1 label label-success clickable"
                                    tabindex="0"
                                    @click="removeTeam(team)"
                                >
                                    {{ team.name }}
                                    <i class="fal fa-times"></i>
                                </button>
                            </span>
                        </div>
                    </div>

                    <l-space v-if="actionItem.type === 'surveyTeamActionItem'" :size="2" />

                    <div class="row">
                        <div class="col-md-6">
                            <l-date-picker
                                wrapper-class="date-picker-wrapper"
                                :label="$t(`surveys.actionItemModal.labelDue`)"
                                :placeholder="''"
                                :value="
                                    actionItem.deadline ? new Date(actionItem.deadline) : new Date()
                                "
                                :on-date-change="
                                    date => {
                                        actionItem.deadline = date;
                                    }
                                "
                            />
                        </div>
                        <div v-if="statusOptions.length > 1" class="col-md-6">
                            <l-select
                                v-model="actionItem.status"
                                :show-help-block="true"
                                :options="statusOptions"
                                :label="$t('surveys.actionItemModal.labelStatus')"
                            />
                        </div>
                    </div>
                    <div v-if="categories().length" class="row">
                        <div class="col-md-6">
                            <l-select
                                v-model="actionItem.category._id"
                                :show-help-block="true"
                                :options="categories()"
                                :label="$t('surveys.actionItemModal.labelCategory')"
                            />
                        </div>
                        <div class="col-md-6"></div>
                    </div>

                    <div class="row">
                        <div class="col-md-12">
                            <l-input-typeahead
                                :label="$t(`surveys.actionItemModal.labelOwner`)"
                                :placeholder="$t(`surveys.actionItemModal.placeholderOwner`)"
                                :data="teamOrUser().filter(t => t.type === 'user')"
                                :on-select="p => addOwners(p)"
                                :on-input-change="typeaheadParticipantOnInputChange"
                            />
                        </div>
                        <div v-if="actionItem.owners" class="col-md-12">
                            <span
                                v-for="owner in actionItem.owners"
                                :key="owner._id"
                                class="tw-mr-1"
                            >
                                <span
                                    class="label label-success clickable"
                                    tabindex="0"
                                    @click="removeOwner(owner)"
                                    @keyup.enter="removeOwner(owner)"
                                >
                                    {{
                                        owner.name ||
                                            owner.fullname ||
                                            owner.firstname + " " + owner.lastname
                                    }}
                                    <i class="fal fa-times"></i>
                                </span>
                            </span>
                        </div>
                    </div>

                    <l-text-alert v-if="error" type="warning" :text="error" />

                    <div class="row">
                        <div class="col-md-offset-6 col-md-3">
                            <button
                                v-show="actionItem._id"
                                class="btn btn-sm btn-danger btn-block"
                                @click="showDeleteConfirmationModal = true"
                            >
                                {{ $t("surveys.actionItemModal.buttonDelete") }}
                            </button>
                        </div>
                        <div class="col-md-3">
                            <button
                                class="pull-right btn btn-sm btn-primary btn-block btn-raised"
                                @click="doUpdateActionItem()"
                            >
                                {{ $t("surveys.actionItemModal.buttonSave") }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="footer"></div>
            </template>
        </l-modal>
        <!-- modal for description -->
        <l-modal
            v-if="showModal && !editMode(actionItem)"
            size="l"
            :close-on-backdrop-click="false"
            @close="closeActionItemModal"
        >
            <template #header>
                <span
                    v-if="actionItem.type !== 'surveyActionItemSuggestion'"
                    class="r-default-margin-s"
                >
                    <l-profile-picture
                        v-for="(owner, idx) in actionItem.owners.slice(0, 5)"
                        :key="`${actionItem._id}_${owner._id}`"
                        size="s"
                        :user="owner"
                        :show-popover-name="true"
                        :class="[{ 'tw--ml-1': idx !== 0 }, 'tw-inline-block']"
                        img-classes="tw-ring-2 tw-ring-white"
                    />

                    {{ actionItem.owners.length > 5 ? `...` : "" }}
                </span>
                <span
                    v-b-popover.hover.bottom="{
                        content: actionItem.content,
                    }"
                >
                    {{ actionItem.content || $t("surveys.actionItemModal.title") }}
                </span>
            </template>
            <template #body>
                <div>
                    <div v-if="actionItem.description && actionItem.description != '<p></p>'">
                        <div v-html="actionItem.description"></div>
                        <hr />
                    </div>

                    <div
                        class="tw-flex tw-space-x-4 tw-text-gray-400 tw-justify-start tw-items-baseline"
                    >
                        <span
                            v-if="actionItem.type !== 'surveyActionItemSuggestion'"
                            class="tw-inline-block"
                        >
                            <i
                                class="tw-mr-1"
                                :class="{
                                    'fal fa-flag tw-text-gray-400': actionItem.status !== 'done',
                                    'fas fa-flag-checkered tw-text-green-700':
                                        actionItem.status === 'done',
                                }"
                            ></i>
                            {{ $t(`surveys.actionItemModal.statusOptions.${actionItem.status}`) }}
                        </span>
                        <span
                            v-if="actionItem.type !== 'surveyActionItemSuggestion'"
                            class="tw-inline-block"
                        >
                            <span
                                v-if="actionItem.deadline && actionItem.status !== 'done'"
                                :title="actionItem.deadline | moment('MMM DD, YYYY')"
                            >
                                <i class="fal fa-calendar tw-mr-1"></i>
                                {{ actionItem.deadline | moment("MMM DD, YYYY") }}
                            </span>
                        </span>

                        <span v-if="categoryName" class="tw-inline-block">
                            <i class="fal fa-tag tw-mr-1"></i>
                            {{ categoryName }}
                        </span>

                        <span>
                            <i class="fal fa-users tw-mr-1"></i>
                            {{ typeLabels(actionItem.type) }}
                            <span
                                v-if="
                                    actionItem.type === 'surveyTeamActionItem' &&
                                        actionItem.localTeams &&
                                        actionItem.localTeams.length
                                "
                            >
                                <span v-for="(team, idx) in actionItem.localTeams" :key="team._id">
                                    {{ team.name }}
                                    {{ `${idx !== actionItem.localTeams.length - 1 ? "," : ""}` }}
                                </span>
                            </span>
                        </span>
                    </div>
                </div>

                <div v-if="actionItem.type === 'surveyActionItemSuggestion'" class="footer">
                    <button class="btn btn-default" type="button" @click="showModal = false">
                        <span>{{ $t("surveys.actionItemModal.buttonCancel") }}</span>
                    </button>
                    <button
                        class="btn btn-primary btn-raised"
                        type="button"
                        @click="copyRecommendation()"
                    >
                        <span>{{ $t("surveys.actionItemModal.buttonCopy") }}</span>
                    </button>
                </div>

                <div v-if="!disabled(actionItem)">
                    <hr />
                    <div class="row">
                        <div class="col-md-3 col-md-offset-9">
                            <button
                                class="btn btn-sm btn-default btn-block btn-raised"
                                @click="toggleEditMode(true)"
                            >
                                {{ $t("surveys.actionItemModal.buttonEditMode") }}
                            </button>
                        </div>
                    </div>
                </div>

                <surveys-insights-action-items-comments
                    v-if="actionItem.type !== 'surveyActionItemSuggestion'"
                    :action-item="actionItem"
                    :survey="survey"
                    @change="setActionItem"
                ></surveys-insights-action-items-comments>
            </template>
        </l-modal>

        <!-- delete confirmation  modal -->

        <l-confirmation-modal
            v-if="showDeleteConfirmationModal"
            :text="$t('surveys.details.helpDeleteActionItemModal')"
            @confirm="deleteActionItem(actionItem)"
            @cancel="showDeleteConfirmationModal = false"
        />
    </div>
    <!-- End Multiple Choice Results -->
</template>

<script>
import { mapGetters } from "vuex";

import icon from "@/img/icons/surveys.png";
import emptyIcon from "@/img/icons/goals.png";
import LProfilePicture from "@/components/LProfilePicture";

import teamPicture from "@/img/icons/team-icon2.png";
import LRichTextEditor from "@/components/LRichTextEditor";
import LInputTypeahead from "@/components/LInputTypeahead";
import LDatePicker from "@/components/LDatePicker";
import LInputText from "@/components/LInputText";
import LSelect from "@/components/LSelect";
import LModal from "@/components/LModal";
import LConfirmationModal from "@/components/LConfirmationModal";
import LSpace from "@/components/LSpace";
import SurveysInsightsActionItemsComments from "./SurveysInsightsActionItemsComments";
import LTextAlert from "@/components/LTextAlert";

export default {
    components: {
        LProfilePicture,
        LModal,
        LConfirmationModal,
        LDatePicker,
        LRichTextEditor,
        LInputText,
        LInputTypeahead,
        LSelect,
        LSpace,
        SurveysInsightsActionItemsComments,
        LTextAlert,
    },
    props: {
        survey: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            error: "",
            expandRecommendation: false,
            actionItem: {
                category: {},
            },
            recommendationType: "all",

            showModal: false, // modal for action-items
            newComment: "",

            showDeleteConfirmationModal: false,
        };
    },
    computed: {
        ...mapGetters({
            rights: "users/current/rights",

            questionList: "surveys/surveys/questionListResult",
            categoryList: "surveys/surveys/categoryListResult",

            usersSearchList: "users/search/list",
            teamsSearchList: "teams/search/list",

            getActionItemById: "surveys/actionItem/getById",
            actionItems: "surveys/actionItem/list",
            recommendations: "surveys/actionItem/recommendations",
            teamSettings: "users/current/teamSettings",
            currentUser: "users/current/user",
            localTeams: "teams/local/listSortedHierarchically",
            lang: "users/current/locale",
        }),
        isOwner() {
            return this.survey.owners?.map(owner => owner._id)?.includes(this.currentUser._id);
        },
        isAdmin() {
            return this.rights?.S_ACCESS_ALL || this.isOwner;
        },
        isManager() {
            return this.localTeams?.some(t => t.role === "manager");
        },
        sortedRecommendations() {
            return this.recommendations
                .filter(r => Boolean(r))
                .sort((a, b) => {
                    if (a.recommended === b.recommended) {
                        return 0;
                    }
                    if (a.recommended === true) {
                        return -1;
                    }
                    return 1;
                });
        },
        sortedActionItemList() {
            const orders = {
                surveyUserActionItem: 1,
                surveyTeamActionItem: 2,
                surveyCompanyActionItem: 3,
            };
            return this.actionItems
                .filter(a => a && a.content != "")
                .sort((a, b) => {
                    if (a.status === b.status) {
                        return orders[a.type] - orders[b.type];
                    }
                    if (a.status === "open") {
                        return -1;
                    }
                    return 1;
                });
        },

        statusOptions() {
            const options = [
                {
                    value: "open",
                    text: this.$t(`surveys.actionItemModal.statusOptions.open`),
                },
            ];
            if (this.actionItem._id) {
                options.push({
                    value: "done",
                    text: this.$t(`surveys.actionItemModal.statusOptions.done`),
                });
            }
            return options;
        },

        typeOptions() {
            // everyone can create user action item
            const options = [
                {
                    value: "surveyUserActionItem",
                    text: this.$t(`surveys.actionItemModal.typeOptions.user`),
                },
                {
                    value: "surveyTeamActionItem",
                    text: this.$t(`surveys.actionItemModal.typeOptions.team`),
                    disabled: !this.isManager && !this.isAdmin,
                },
                {
                    value: "surveyCompanyActionItem",
                    text: this.$t(`surveys.actionItemModal.typeOptions.company`),
                    disabled: !this.isAdmin,
                },
            ];

            return options;
        },
        visibilityOptions() {
            const options = [];

            if (this.isAdmin) {
                options.push({
                    value: "public",
                    text: this.$t(`surveys.actionItemModal.visibilityOptions.public`),
                });
            }

            if (this.actionItem?.type !== "surveyCompanyActionItem") {
                options.push({
                    value: "limited",
                    text: this.$t(`surveys.actionItemModal.visibilityOptions.limited`),
                });
            }

            return options;
        },
        categoryName() {
            return this.actionItem?.category?.name;
        },
    },
    watch: {
        multi: {
            immediate: true,
            handler(multi) {
                this.$emit("change", multi?.minObsAnonymity);
            },
        },
        "$route.params.id": {
            immediate: true,
            handler(surveyId) {
                this.$store
                    .dispatch("surveys/actionItem/list", {
                        payload: { surveyId },
                    })
                    .then(() => {
                        const id = this.$route.query?.actionItemId;
                        if (id && id !== "new") {
                            this.setActionItem(id);
                        }
                    });
            },
        },
        "$route.query": {
            immediate: true,
            handler(newQuery, oldQuery) {
                if (
                    newQuery &&
                    newQuery.actionItemId &&
                    (!oldQuery || newQuery.actionItemId != oldQuery.actionItemId)
                ) {
                    this.setActionItem(newQuery.actionItemId);
                }
                if (newQuery && newQuery.actionItemId) {
                    if (newQuery.actionItemId === "new") {
                        this.actionItem = {
                            category: {},
                            creator: {
                                _id: this.currentUser._id,
                                firstname: this.currentUser.firstname,
                                lastname: this.currentUser.lastname,
                            },
                            owners: [
                                {
                                    _id: this.currentUser._id,
                                    firstname: this.currentUser.firstname,
                                    lastname: this.currentUser.lastname,
                                },
                            ],
                            status: "open",
                            type: "surveyUserActionItem",
                            visibility: "limited",
                        };
                    }
                    this.showModal = true;
                } else {
                    this.showModal = false;
                }
            },
        },
        "actionItem.type": {
            immediate: true,
            handler(type) {
                if (type === "surveyCompanyActionItem") {
                    this.actionItem.visibility = "public";
                }
            },
        },
        recommendationType: {
            immediate: true,
            handler(type) {
                this.$store
                    .dispatch("surveys/actionItem/listRecommendations", {
                        payload: { surveyId: this.survey._id, type: type, lang: this.lang },
                    })
                    .then(() => {
                        this.setActionItem(this.$route.query.actionItemId);
                    });
            },
        },
    },

    created() {
        this.icon = icon;
        this.emptyIcon = emptyIcon;
    },
    methods: {
        showError(error) {
            this.error = error;
            setTimeout(() => {
                this.error = "";
            }, 5000);
        },
        disabled(item = {}) {
            // user is the creator
            if (item.creator?._id === this.currentUser._id) {
                return false;
            }
            if (item.type === "surveyActionItemSuggestion") {
                return true;
            }
            if (!item.owners?.map(o => o._id).includes(this.currentUser._id)) {
                return true; // onwer is not me
            }
            return false;
        },
        editMode(actionItem = {}) {
            if (actionItem.editMode || !actionItem._id) {
                return true;
            } else {
                return false;
            }
        },
        toggleEditMode(enabled) {
            this.actionItem.editMode = enabled;
            this.$forceUpdate();
        },
        setActionItem(id) {
            this.actionItem = this.recommendations?.find(r => r._id == id);

            if (!this.actionItem?._id) {
                this.actionItem = this.getActionItemById(id);
            }

            if (this.actionItem && !this.actionItem.category) {
                this.actionItem.category = { _id: null };
            }
        },
        closeActionItemModal() {
            this.$router.push({ query: { actionItemId: undefined } });
        },
        openActionItemModal(item) {
            this.$router.push({ query: { actionItemId: item?._id || "new" } });
        },
        copyRecommendation() {
            delete this.actionItem._id;
            this.actionItem.type = "surveyUserActionItem";
            this.actionItem.creator = {
                _id: this.currentUser._id,
                firstname: this.currentUser.firstname,
                lastname: this.currentUser.lastname,
            };
            this.actionItem.owners = [
                {
                    _id: this.currentUser._id,
                    firstname: this.currentUser.firstname,
                    lastname: this.currentUser.lastname,
                },
            ];
            this.actionItem.status = "open";
            this.actionItem.visibility = "limited";
            this.actionItem.comments = [];

            this.toggleEditMode(true);
        },
        typeaheadParticipantOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
                this.$store.dispatch("teams/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
                this.$store.dispatch("teams/search/reset");
            }
        },
        addTeams(team) {
            if (!this.actionItem.localTeams) {
                this.actionItem.localTeams = [];
            }
            const localTeams = this.actionItem.localTeams;
            // check existense.
            if (localTeams?.some(l => team._id == l)) {
                return;
            }
            // check permissions FIXME this is not right
            if (!this.isAdmin && !this.isManager) {
                return;
            }
            this.actionItem.localTeams.push(team);
        },
        removeTeam(team) {
            this.actionItem.localTeams = this.actionItem.localTeams?.filter(o => o._id != team._id);
        },
        removeOwner(owner) {
            if (this.actionItem.owners?.length === 1) {
                this.showError(this.$t("surveys.actionItem.errors.atLeastOneOwner"));
                return;
            }
            this.actionItem.owners = this.actionItem.owners?.filter(o => o._id != owner._id);
        },
        addOwners(owner) {
            if (!this.actionItem.owners) {
                this.actionItem.owners = [];
            }
            // check existense.
            if (this.actionItem.owners.some(o => o._id == owner._id)) {
                return;
            }
            this.actionItem.owners.push(owner);
        },
        doUpdateActionItem() {
            if (!this.actionItem.type) {
                this.actionItem.type = "surveyUserActionItem"; // default value
            }
            if (!this.actionItem.content) {
                this.showError(this.$t("surveys.actionItem.errors.emptyTitle"));
                return;
            }
            if (!this.actionItem._id) {
                this.$store
                    .dispatch("surveys/actionItem/create", {
                        payload: {
                            ...this.actionItem,
                            status: "open",
                        },
                        surveyId: this.survey._id,
                    })
                    .then(response => {
                        if (response?.data?._id) {
                            this.$router.push({ query: { actionItemId: response.data._id } });

                            // back to display mode
                            this.toggleEditMode(false);
                        }
                    })
                    .catch(({ response }) => {
                        if (response?.data?.message) {
                            this.showError(response.data.message);
                        }
                    });
            } else {
                this.$store
                    .dispatch("surveys/actionItem/update", {
                        payload: this.actionItem,
                    })
                    .then(() => {
                        // back to display mode
                        this.toggleEditMode(false);
                    })
                    .catch(({ response }) => {
                        if (response?.data?.message) {
                            this.showError(response.data.message);
                        }
                    });
            }
        },
        deleteActionItem(actionItem) {
            this.$store
                .dispatch("surveys/actionItem/delete", {
                    payload: {
                        survey: this.survey._id,
                        actionItem,
                    },
                })
                .then(() => {
                    this.$router.push({ query: { actionItemId: "" } });
                    this.showDeleteConfirmationModal = false;
                });
        },

        questions() {
            return this.survey.questions?.map(q => ({
                value: q._id,
                text: q.skill?.name?.replace("%ACCOUNTNAME%", this.companyName),
            }));
        },
        categories() {
            const questions = this.survey.questions;

            var categories = this.survey.questions.filter(
                (e, i) => questions.findIndex(a => a["category"] === e["category"]) === i,
            );

            return (
                categories
                    .filter(c => c.category)
                    .map(c => ({ value: c.category, text: c.categoryName })) || []
            );
        },
        changeQuestion(question) {
            this.actionItem.skill = question;
        },
        teamOrUser() {
            const existedUsers = [];
            const existedTeams = [];
            return [
                ...this.usersSearchList
                    .filter(user => !existedUsers.map(p => p._id).includes(user._id))
                    .map(user => ({
                        _id: user._id,
                        type: "user",
                        firstname: user.firstname,
                        lastname: user.lastname,
                        name: user.firstname + " " + user.lastname,
                        desc: user.teamRole.title,
                        picture: user.picture,
                    })),
                ...this.teamsSearchList
                    .filter(team => !existedTeams.map(t => t._id).includes(team._id))
                    .map(team => ({
                        _id: team._id,
                        type: "team",
                        picture: teamPicture,
                        name: team.name,
                    })),
            ];
        },

        typeLabels(type) {
            if (type == "surveyUserActionItem") {
                return this.$t(`surveys.actionItemModal.typeOptions.user`);
            } else if (type == "surveyTeamActionItem") {
                return this.$t(`surveys.actionItemModal.typeOptions.team`);
            } else {
                return this.$t(`surveys.actionItemModal.typeOptions.company`);
            }
        },
    },
};
</script>

<style scoped>
.l-date-picker >>> .date-picker-wrapper {
    margin-top: 0px;
    padding-bottom: 20px;
}
.l-date-picker >>> .date-picker-wrapper .input-group {
    width: 100%;
}
</style>
